import React, { useState, useEffect, useRef } from 'react';
import { format } from 'date-fns';
import { 
User, MessageSquare, 
} from 'lucide-react';
import {  useNavigate } from 'react-router-dom';

const MessageDrawer = ({ isOpen, onClose, chats, onChatSelect }) => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef(null);

  // Filtered and sorted chats based on search and recency
  const filteredChats = chats
    .filter(chat => 
      chat.otherParty.firstName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      chat.otherParty.lastName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (chat.lastMessage && chat.lastMessage.content.toLowerCase().includes(searchQuery.toLowerCase()))
    )
    .sort((a, b) => new Date(b.lastMessage?.createdAt || 0) - new Date(a.lastMessage?.createdAt || 0));

  // Keyboard event handlers
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // Auto-focus input when drawer opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Reset state when drawer closes
  useEffect(() => {
    if (!isOpen) {
      setSearchQuery('');
      setSelectedIndex(0);
    }
  }, [isOpen]);

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((prev) => 
        prev < filteredChats.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((prev) => prev > 0 ? prev - 1 : prev);
    } else if (e.key === 'Enter' && filteredChats.length > 0) {
      const selectedChat = filteredChats[selectedIndex];
      navigate(`/chat/${selectedChat.connectionId}`);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="min-h-screen px-4 text-center">
        {/* Backdrop */}
        <div 
          className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm transition-opacity" 
          onClick={onClose} 
        />

        {/* Dialog */}
        <div className="inline-block w-full max-w-2xl my-16 overflow-hidden text-left align-middle transition-all transform">
          <div className="relative bg-white rounded-2xl shadow-2xl ring-1 ring-gray-200">
            {/* Search Input */}
            <div className="flex items-center px-4 border-b border-gray-200">
              <div className="flex items-center min-w-0 flex-1 px-2">
                <MessageSquare className="w-5 h-5 text-gray-400 flex-shrink-0" />
                <input
                  ref={inputRef}
                  type="text"
                  placeholder="Search messages..."
                  className="w-full px-3 py-4 bg-transparent border-0 focus:ring-0 text-gray-900 
                    placeholder-gray-400 text-sm outline-none"
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setSelectedIndex(0);
                  }}
                  onKeyDown={handleKeyDown}
                />
              </div>
              <kbd className="hidden sm:inline-flex items-center px-2 py-1.5 text-xs font-semibold 
                text-gray-500 bg-gray-100 rounded-md border border-gray-300">
                ESC
              </kbd>
            </div>

            {/* Results */}
            <div className="max-h-96 overflow-y-auto overscroll-contain py-2">
              {filteredChats.length === 0 ? (
                <div className="px-6 py-8 text-center">
                  <MessageSquare className="mx-auto h-6 w-6 text-gray-400" />
                  <p className="mt-2 text-sm text-gray-500">
                    {searchQuery 
                      ? `No messages found for "${searchQuery}"` 
                      : "No messages yet"
                    }
                  </p>
                </div>
              ) : (
                filteredChats.map((chat, index) => (
                  <button
                    key={chat.connectionId}
                    className={`w-full text-left px-4 py-3 flex items-center space-x-3 
                      transition-colors duration-100
                      ${selectedIndex === index 
                        ? 'bg-blue-50 text-blue-600' 
                        : 'text-gray-700 hover:bg-gray-50'
                      }`}
                    onClick={() => {
                      navigate(`/chat/${chat.connectionId}`);
                      onClose();
                    }}
                    onMouseEnter={() => setSelectedIndex(index)}
                  >
                    {/* Avatar or Placeholder */}
                    {chat.otherParty.avatar ? (
                      <img
                        src={chat.otherParty.avatar}
                        alt={chat.otherParty.firstName}
                        className={`h-8 w-8 rounded-full object-cover flex-shrink-0
                          ${selectedIndex === index ? 'border-2 border-blue-500' : ''}`}
                      />
                    ) : (
                      <div className={`h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0
                        ${selectedIndex === index ? 'border-2 border-blue-500' : ''}`}>
                        <User className="h-4 w-4 text-blue-600" />
                      </div>
                    )}

                    {/* Chat Details */}
                    <div className="flex-1 flex flex-col">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium truncate">
                          {chat.otherParty.firstName} {chat.otherParty.lastName}
                        </span>
                        {chat.lastMessage && (
                          <span className="text-xs text-gray-500">
                            {format(new Date(chat.lastMessage.createdAt), 'HH:mm')}
                          </span>
                        )}
                      </div>
                      {chat.lastMessage && (
                        <p className={`text-xs truncate ${
                          selectedIndex === index 
                            ? 'text-blue-500' 
                            : 'text-gray-500'
                        }`}>
                          {chat.lastMessage.content}
                        </p>
                      )}
                    </div>

                    {/* Unread Count */}
                    {chat.unreadCount > 0 && (
                      <span className="ml-2 bg-blue-100 text-blue-800 text-xs font-medium px-2 py-0.5 rounded-full">
                        {chat.unreadCount}
                      </span>
                    )}

                    {/* Enter to Select */}
                    {selectedIndex === index && (
                      <kbd className="hidden sm:inline-flex items-center px-1.5 py-0.5 text-xs 
                        font-semibold text-gray-400 border border-gray-200 rounded">
                        ↵
                      </kbd>
                    )}
                  </button>
                ))
              )}
            </div>

            {/* Footer */}
            <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 text-xs text-gray-500">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <span>↑↓ to navigate</span>
                  <span>↵ to select</span>
                  <span>ESC to close</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MessageDrawer;